import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import { CancelOrderMessageType } from "@redotech/redo-model/conversation/message-parsers/cancel-order-message-parser";
import { ShopifyOrder } from "@redotech/redo-model/shopify-order";
import { sinkPromise } from "@redotech/util/promise";
import classNames from "classnames";
import { memo, useState } from "react";
import { RedoButton } from "../../arbiter-components/buttons/redo-button";
import { RedoSpinner } from "../../arbiter-components/loading/redo-spinner";
import { Divider } from "../../divider";
import { Flex } from "../../flex";
import { Text } from "../../text";
import { GetOrderDetailsFn, HandleCancelOrderFn } from "../common";
import * as cancelOrderToolMessageCss from "./cancel-order-tool-message.module.css";
import { ChatContentRenderer } from "./chat-content-renderer";
import {
  GenericConfirmedState,
  GenericRejectedState,
} from "./chat-content-states";
import * as css from "./subscribe-to-back-in-stock-tool-message.module.css";
import { getConfirmedStateContent } from "./tool-message-utils";

interface CancelOrderToolMessageProps {
  message: CancelOrderMessageType;
  onCancelOrder?: HandleCancelOrderFn;
  readOnly?: boolean;
  getOrderDetails?: GetOrderDetailsFn;
  conversationId: string;
}

const CancelOrderPendingState = memo(function CancelOrderPendingState({
  message,
  onCancelOrder,
  readOnly = false,
  getOrderDetails,
  conversationId,
}: CancelOrderToolMessageProps) {
  const { structuredContent } = message;
  const orderLoad = useLoad(async () => {
    if (!getOrderDetails || !structuredContent.orderId) {
      return null;
    }
    return await getOrderDetails({
      orderId: structuredContent.orderId,
      conversationId,
    });
  }, [getOrderDetails, structuredContent.orderId, conversationId]);

  const [cancelButtonPending, setCancelButtonPending] = useState(false);
  const [confirmButtonPending, setConfirmButtonPending] = useState(false);
  const confirmCancel = useHandler(async (confirmCancel: boolean) => {
    if (confirmCancel) {
      setConfirmButtonPending(true);
    } else {
      setCancelButtonPending(true);
    }
    await onCancelOrder?.({
      conversationId,
      pendingToolCallId: message.pendingToolCallId ?? "",
      confirmationData: { ...structuredContent, confirmCancel },
    });
  });

  const isCompleted = message.customerInputContent?.cancelled !== undefined;

  if (orderLoad.pending) {
    return (
      <Flex align="center" justify="center" py="md">
        <RedoSpinner size="md" />
      </Flex>
    );
  }

  return (
    <Flex
      align="flex-end"
      as="form"
      className={classNames(
        css.subscribeToBackInStockBubbleContainer,
        css.messageSideLeft,
      )}
      dir="row"
      gap="md"
      px="lg"
      py="md"
    >
      <Flex dir="column" gap="xl" w="full">
        <OrderDetailsCard shopifyOrder={orderLoad.value?.shopify} />
        {isCompleted ? (
          <Flex alignSelf="center" dir="column" gap="sm">
            <Text fontSize="md" fontWeight="semibold" textColor="primary">
              {message.customerInputContent?.cancelled
                ? "Order cancelled successfully"
                : "Order was not cancelled"}
            </Text>
          </Flex>
        ) : (
          <Flex gap="sm" ml="auto">
            <RedoButton
              disabled={readOnly || cancelButtonPending || confirmButtonPending}
              hierarchy="secondary"
              onClick={() => {
                sinkPromise(confirmCancel(false));
              }}
              pending={cancelButtonPending}
              text="No"
            />
            <RedoButton
              buttonClassName={cancelOrderToolMessageCss.button}
              disabled={readOnly || cancelButtonPending || confirmButtonPending}
              hierarchy="primary"
              onClick={() => {
                sinkPromise(confirmCancel(true));
              }}
              pending={confirmButtonPending}
              text="Yes, cancel order"
            />
          </Flex>
        )}
      </Flex>
    </Flex>
  );
});

export const CancelOrderToolMessage = memo(function CancelOrderToolMessage(
  props: CancelOrderToolMessageProps,
) {
  const { message, onCancelOrder, readOnly, getOrderDetails, conversationId } =
    props;
  const orderNumber = message.structuredContent?.orderId || "Order";

  const { title: confirmedTitle, subtitle: confirmedSubtitle } =
    getConfirmedStateContent(
      message,
      "Order cancelled successfully",
      "Order not cancelled.",
      `Order ${orderNumber} has been cancelled`,
    );

  return (
    <ChatContentRenderer
      contentByState={{
        pending: (
          <CancelOrderPendingState
            conversationId={conversationId}
            getOrderDetails={getOrderDetails}
            message={message}
            onCancelOrder={onCancelOrder}
            readOnly={readOnly}
          />
        ),
        confirmed: (
          <GenericConfirmedState
            subtitle={confirmedSubtitle}
            title={confirmedTitle}
          />
        ),
        rejected: (
          <GenericRejectedState
            subtitle="The order remains active and will be processed as scheduled"
            title="Order was not cancelled"
          />
        ),
      }}
      message={message}
    />
  );
});

const OrderDetailsCard = memo(function OrderDetailsCard({
  shopifyOrder,
}: {
  shopifyOrder: ShopifyOrder | undefined;
}) {
  if (!shopifyOrder) {
    return (
      <Flex dir="column" gap="lg">
        <Text fontSize="md" fontWeight="semibold" textColor="primary">
          Order not found
        </Text>
      </Flex>
    );
  }

  return (
    <Flex dir="column" gap="lg">
      <Flex dir="column" gap="sm">
        <Text fontSize="md" fontWeight="semibold" textColor="primary">
          Confirm order cancellation
        </Text>
      </Flex>

      <Flex dir="column" gap="md">
        <Flex justify="space-between">
          <Text fontSize="sm" fontWeight="medium" textColor="secondary">
            Order number
          </Text>
          <Text fontSize="sm" fontWeight="medium">
            {shopifyOrder.name || shopifyOrder.id}
          </Text>
        </Flex>

        {shopifyOrder.email && (
          <Flex justify="space-between">
            <Text fontSize="sm" fontWeight="medium" textColor="secondary">
              Customer email
            </Text>
            <Text fontSize="sm" fontWeight="medium">
              {shopifyOrder.email}
            </Text>
          </Flex>
        )}

        {shopifyOrder.total_price && (
          <Flex justify="space-between">
            <Text fontSize="sm" fontWeight="medium" textColor="secondary">
              Total amount
            </Text>
            <Text fontSize="sm" fontWeight="medium">
              ${shopifyOrder.total_price}
            </Text>
          </Flex>
        )}
      </Flex>

      {shopifyOrder.line_items && shopifyOrder.line_items.length > 0 && (
        <>
          <Divider />
          <Flex dir="column" gap="md">
            <Text fontSize="sm" fontWeight="medium" textColor="secondary">
              Items ({shopifyOrder.line_items.length})
            </Text>
            <Flex dir="column" gap="sm">
              {shopifyOrder.line_items
                .slice(0, 3)
                .map((item: any, index: number) => (
                  <Flex align="flex-start" justify="space-between" key={index}>
                    <Flex dir="column" flex={1} gap="xxs">
                      <Text fontSize="sm" fontWeight="medium">
                        {item.title}
                      </Text>
                      {item.variant_title &&
                        item.variant_title !== "Default Title" && (
                          <Text fontSize="xs" textColor="secondary">
                            {item.variant_title}
                          </Text>
                        )}
                    </Flex>
                    <Flex align="flex-end" dir="column" gap="xxs">
                      <Text fontSize="sm" fontWeight="medium">
                        ${item.price}
                      </Text>
                      <Text fontSize="xs" textColor="secondary">
                        Qty: {item.quantity}
                      </Text>
                    </Flex>
                  </Flex>
                ))}
              {shopifyOrder.line_items.length > 3 && (
                <Text fontSize="xs" textColor="secondary">
                  ... and {shopifyOrder.line_items.length - 3} more items
                </Text>
              )}
            </Flex>
          </Flex>
        </>
      )}
    </Flex>
  );
});
